'use client';

import { createPortal } from 'react-dom';
import { X, Link2, LogOut, Info, LayoutDashboard, Users } from 'lucide-react';
import useAuthStore from '@/store/useAuthStore';
import STORAGE_KEYS from '@/constant/storage';
import report, { ReportEvent } from '@/lib/report';

interface LinkParentAccountModalProps {
  visible: boolean;
  onClose: () => void;
}

const PRIMARY_BUTTON_CLASS = 'display-text-semi inline-flex w-full items-center justify-center rounded-xl bg-[#42C62C] py-3 text-sm text-white transition-colors hover:bg-[#38A824] sm:w-auto sm:min-w-[220px]';
const SECONDARY_BUTTON_CLASS = 'display-text-semi w-full rounded-xl bg-[#ECEEF2] py-3 text-sm text-[#2F3A4A] transition-colors hover:bg-[#E2E6EC] sm:w-auto sm:min-w-[220px]';

export default function LinkParentAccountModal({ visible, onClose }: LinkParentAccountModalProps) {
  const { signOut } = useAuthStore();

  if (!visible || typeof window === 'undefined') {
    return null;
  }

  const handleSignOut = async () => {
    report(ReportEvent.USER_QUEST_AVATAR_SIGN_OUT_CLICK, {});
    await signOut();
    localStorage.removeItem(STORAGE_KEYS.ONBOARD_DATA);
    localStorage.removeItem(STORAGE_KEYS.USER);
    onClose();
    window.location.href = '/';
  };

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-3 sm:p-4"
      style={{ backgroundColor: 'rgba(15, 23, 42, 0.55)', zIndex: 1200 }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-[760px] max-h-[92vh] overflow-hidden rounded-[28px] border border-[#E8DDCF] bg-[#F8F5EE] px-5 pb-6 pt-6 shadow-[0_28px_90px_rgba(15,23,42,0.22)] animate-fade-in sm:px-7 sm:pb-7 sm:pt-7 md:px-8 md:pb-8 md:pt-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative max-h-[92vh] overflow-y-auto pb-1">
        <div className="relative flex items-start justify-center">
          <div className="flex min-w-0 items-center justify-center gap-3 pr-10">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#FFEFD9] sm:h-12 sm:w-12">
              <Link2 size={18} className="text-[#B26A13] sm:h-5 sm:w-5" />
            </div>
            <h3 className="display-text-bold text-center text-2xl leading-tight tracking-tight text-[#1E1E20] sm:text-[1.85rem]">
              Parent Link Request
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="absolute right-0 top-0 flex h-9 w-9 items-center justify-center rounded-full text-[#667085] transition-colors hover:bg-[#EEE9DF] hover:text-[#111827] sm:h-10 sm:w-10"
            aria-label="Close parent link dialog"
          >
            <X className="h-4.5 w-4.5" />
          </button>
        </div>

        <div className="relative mt-5 rounded-[24px] bg-white/82 px-4 py-5 shadow-[0_8px_22px_rgba(93,69,40,0.08)] sm:mt-6 sm:px-5 sm:py-6 md:mt-7 md:px-6 md:py-7">
          <div className="relative flex flex-col items-center text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#FFF4DE] sm:h-20 sm:w-20">
              <Users size={30} className="text-[#B26A13] sm:h-9 sm:w-9" />
            </div>
            <p className="display-text mt-3 max-w-[480px] text-[0.98rem] leading-7 text-[#374151] sm:mt-4 sm:text-[1.05rem]">
              To link your progress and share your learning journey, your parent must first register their own account.
            </p>
          </div>
        </div>

        <div className="mt-5 grid gap-3 sm:mt-6 sm:gap-4 md:grid-cols-2">
          <div className="rounded-[22px] bg-white/78 p-4 shadow-[0_8px_20px_rgba(93,69,40,0.08)] sm:p-5">
            <div className="mb-3 flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FFEFD9] sm:h-10 sm:w-10">
                <LayoutDashboard size={18} className="text-[#FBAF2C]" />
              </div>
              <p className="display-text-bold text-[15px] text-[#1E1E20] sm:text-base">Step 1</p>
            </div>
            <p className="display-text text-[13px] leading-5 text-[#4B5563] sm:text-sm sm:leading-6">
              Once registered, they can go to the <span className="display-text-bold text-[#B26A13]">Manage Students</span> section in their dashboard.
            </p>
          </div>

          <div className="rounded-[22px] bg-white/78 p-5 shadow-[0_8px_20px_rgba(93,69,40,0.08)]">
            <div className="mb-3 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFEFD9]">
                <Link2 size={18} className="text-[#FBAF2C]" />
              </div>
              <p className="display-text-bold text-[15px] text-[#1E1E20] sm:text-base">Step 2</p>
            </div>
            <p className="display-text text-sm leading-6 text-[#4B5563]">
              They can use the <span className="display-text-bold text-[#B26A13]">Link</span> feature to find you and sync your accounts together.
            </p>
          </div>
        </div>

        <div className="mt-5 flex items-center justify-center sm:mt-6">
          <div className="inline-flex max-w-full items-start gap-2.5 rounded-[18px] border border-[#F5D7A6] bg-[#FFF8E7] px-4 py-3 text-center sm:items-center sm:gap-3 sm:rounded-full sm:px-5">
            <Info size={18} className="shrink-0 text-[#B78100]" />
            <p className="display-text text-[13px] font-semibold leading-5 text-[#8B6500] sm:text-sm">
              Need to register? You&apos;ll need to sign out of this account first.
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:mt-7 sm:flex-row sm:items-center sm:justify-center">
          <button
            type="button"
            onClick={handleSignOut}
            className={PRIMARY_BUTTON_CLASS}
          >
            <span className="flex items-center justify-center gap-2 text-sm">
              <LogOut size={14} />
              Log Out
            </span>
          </button>
          <button
            type="button"
            onClick={onClose}
            className={SECONDARY_BUTTON_CLASS}
          >
            Cancel
          </button>
        </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
